import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

// Format date in French
export function formatDate(date, options = {}) {
    if (!date) return "";
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "short",
        year: "numeric",
        ...options,
    });
}

// Format datetime in French
export function formatDateTime(date) {
    if (!date) return "";
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

// Format relative time in French
export function formatRelativeTime(date) {
    if (!date) return "";
    const d = typeof date === "string" ? new Date(date) : date;
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return formatDate(d);
}

// Ticket type configuration
export const TICKET_TYPES = [
    { id: "incident", label: "Incident", icon: "AlertTriangle", description: "Signaler un dysfonctionnement", defaultPriority: "high", category: "incidents" },
    { id: "problem", label: "Problème", icon: "Bug", description: "Problème récurrent ou majeur", defaultPriority: "medium", category: "incidents" },
    { id: "security", label: "Sécurité", icon: "ShieldAlert", description: "Faille ou alerte de sécurité", defaultPriority: "critical", category: "incidents" },
    { id: "password_reset", label: "Réinitialisation mot de passe", icon: "KeyRound", description: "Mot de passe oublié ou compte verrouillé", defaultPriority: "medium", category: "it" },
    { id: "access_request", label: "Demande d'accès", icon: "Key", description: "Accès à un outil ou fichier", defaultPriority: "medium", category: "it" },
    { id: "software_install", label: "Installation logicielle", icon: "DownloadCloud", description: "Nouveau logiciel", defaultPriority: "low", category: "it" },
    { id: "software_update", label: "Mise à jour", icon: "RefreshCw", description: "Mise à jour de logiciel", defaultPriority: "low", category: "it" },
    { id: "new_equipment", label: "Nouveau matériel", icon: "Monitor", description: "Demande d'équipement", defaultPriority: "medium", category: "it" },
    { id: "defective_equipment", label: "Matériel défectueux", icon: "AlertOctagon", description: "Signaler un équipement dégradé", defaultPriority: "medium", category: "it" },
    { id: "equipment_loan", label: "Prêt / déplacement matériel", icon: "PackageCheck", description: "Réserver du matériel pour un déplacement", defaultPriority: "low", category: "it" },
    { id: "general_it_request", label: "Demande générale IT", icon: "MessageSquarePlus", description: "Toute demande informatique ne correspondant pas aux autres catégories", defaultPriority: "medium", category: "it" },
    { id: "license_request", label: "Demande de licence", icon: "KeyRound", description: "Demander une licence pour un logiciel", defaultPriority: "medium", category: "it" },
    { id: "change", label: "Changement", icon: "GitCommit", description: "Demande de modification d'infrastructure", defaultPriority: "medium", category: "it" },
    { id: "onboarding", label: "Arrivée", icon: "UserPlus", description: "Nouvel employé", defaultPriority: "high", category: "hr" },
    { id: "offboarding", label: "Départ", icon: "UserMinus", description: "Départ d'un employé", defaultPriority: "high", category: "hr" },
    { id: "department_transfer", label: "Transfert de département", icon: "ArrowRightLeft", description: "Changement de département d'un collaborateur", defaultPriority: "medium", category: "hr" },
    { id: "service_request", label: "Demande de service", icon: "Headphones", description: "Demande générale", defaultPriority: "low", category: "general" },
    { id: "question", label: "Question", icon: "HelpCircle", description: "Demande d'information", defaultPriority: "low", category: "general" },
    { id: "other", label: "Autre", icon: "MoreHorizontal", description: "Autre demande", defaultPriority: "low", category: "general" },
];

export const TICKET_CATEGORIES = [
    { id: "incidents", label: "Incidents & Problèmes" },
    { id: "it", label: "Demandes IT" },
    { id: "hr", label: "Collaborateurs" },
    { id: "general", label: "Général" },
];

export const PRIORITIES = [
    { id: "low", label: "Basse", color: "badge-priority-low" },
    { id: "medium", label: "Moyenne", color: "badge-priority-medium" },
    { id: "high", label: "Haute", color: "badge-priority-high" },
    { id: "critical", label: "Critique", color: "badge-priority-critical" },
];

export const STATUSES = [
    { id: "new", label: "Nouveau", color: "badge-status-new" },
    { id: "draft", label: "Brouillon", color: "badge-status-draft" },
    { id: "open", label: "Ouvert", color: "badge-status-open" },
    { id: "pending_approval", label: "En attente d'approbation", color: "badge-status-pending_approval" },
    { id: "in_progress", label: "En cours", color: "badge-status-in_progress" },
    { id: "pending_user", label: "En attente utilisateur", color: "badge-status-pending_user" },
    { id: "resolved", label: "Résolu", color: "badge-status-resolved" },
    { id: "closed", label: "Clôturé", color: "badge-status-closed" },
    { id: "cancelled", label: "Annulé", color: "badge-status-cancelled" },
];

export const ROLES = [
    { id: "admin", label: "Administrateur" },
    { id: "agent", label: "Agent IT" },
    { id: "coordinator", label: "Coordinateur" },
    { id: "approver", label: "Approbateur" },
    { id: "user", label: "Utilisateur" },
];

// Get status/priority config by id
export function getStatusConfig(statusId) {
    return STATUSES.find((s) => s.id === statusId) || STATUSES[0];
}

export function getPriorityConfig(priorityId) {
    return PRIORITIES.find((p) => p.id === priorityId) || PRIORITIES[0];
}

export function getTicketTypeConfig(typeId) {
    return TICKET_TYPES.find((t) => t.id === typeId) || TICKET_TYPES[TICKET_TYPES.length - 1];
}

// Get initials from name
export function getInitials(firstName, lastName) {
    return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
}

// Status transition matrix (mirrors backend enums.py)
export const ALLOWED_TRANSITIONS = {
    new:               ["open", "in_progress", "pending_approval", "cancelled"],
    draft:             ["open", "cancelled"],
    open:              ["in_progress", "pending_approval", "pending_user", "resolved", "closed", "cancelled"],
    pending_approval:  ["in_progress", "open", "cancelled"],
    in_progress:       ["pending_user", "resolved", "closed", "open", "cancelled"],
    pending_user:      ["in_progress", "resolved", "closed", "cancelled"],
    resolved:          ["closed", "in_progress", "open"],
    closed:            [],
    cancelled:         [],
};

export function getValidTransitions(currentStatus) {
    const allowed = ALLOWED_TRANSITIONS[currentStatus] || [];
    return STATUSES.filter((s) => s.id === currentStatus || allowed.includes(s.id));
}

// Ancien collaborateur : compte Microsoft 365 supprimé/désactivé (conservé en base pour le suivi)
export function hasLeftCompany(user) {
    return !!user && (user.entra_status === "deleted" || user.entra_status === "disabled" || !!user.left_company_at);
}
